import * as XLSX from "xlsx";
import { IAssetPayload } from "./models";

function normalizeHeader(h: string): string {
  return String(h)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

// Maps normalized spreadsheet headers -> asset fields. Covers the column names
// used in "SEMA'S ASSET REGISTER" plus common variants.
const HEADER_MAP: Record<string, keyof IAssetPayload> = {
  category: "category",
  assetcategory: "category",
  type: "category",

  assetno: "assetNo",
  assetnumber: "assetNo",
  assetid: "assetNo",
  asset: "assetNo",
  tagno: "assetNo",

  status: "status",
  condition: "status",

  descriptionofassetincludingmodelnumber: "description",
  descriptionofasset: "description",
  description: "description",
  assetdescription: "description",
  modelnumber: "description",
  model: "description",

  serialnumber: "serialNumber",
  serialno: "serialNumber",
  serial: "serialNumber",
  sn: "serialNumber",

  supplier: "supplier",
  vendor: "supplier",
  source: "supplier",

  dateofpurchase: "purchaseDate",
  purchasedate: "purchaseDate",
  dateofacquisition: "purchaseDate",
  acquisitiondate: "purchaseDate",
  dateacquired: "purchaseDate",

  totalcostofassetgrossofacquisitions: "totalCost",
  totalcostofasset: "totalCost",
  totalcost: "totalCost",
  grosscost: "totalCost",
  amount: "totalCost",
  cost: "totalCost",
  price: "totalCost",

  usdeuro: "currency",
  currency: "currency",

  notes: "notes",
  comments: "notes",
  comment: "notes",
  remarks: "notes",
  availability: "notes",
};

// Normalize a free-text status from the sheet into one of our enum values.
function normalizeStatus(value: string): string {
  const v = value.toLowerCase();
  if (v.includes("dispos")) return "Disposed";
  if (v.includes("miss")) return "Missing";
  if (v.includes("donat")) return "Donated";
  if (v.includes("fault") || v.includes("not functional")) return "Faulty";
  if (v.includes("function")) return "Functional";
  return "Functional";
}

function excelDateToISO(val: any): string {
  if (val instanceof Date) return val.toISOString().slice(0, 10);
  if (typeof val === "number" && val > 0) {
    const js = new Date((val - 25569) * 86400000);
    return js.toISOString().slice(0, 10);
  }
  return String(val).trim();
}

export async function parseAssetsFromExcel(
  file: File
): Promise<IAssetPayload[]> {
  const buf = await file.arrayBuffer();
  const workbook = XLSX.read(new Uint8Array(buf), {
    type: "array",
    cellDates: true,
  });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const raw: Record<string, any>[] = XLSX.utils.sheet_to_json(sheet, {
    defval: "",
  });

  const assets: IAssetPayload[] = raw
    .map((row) => {
      const mapped: Partial<IAssetPayload> = {};
      for (const [key, val] of Object.entries(row)) {
        const field = HEADER_MAP[normalizeHeader(key)];
        if (!field) continue;
        if (val === "" || val === null || val === undefined) continue;

        if (field === "purchaseDate") {
          mapped.purchaseDate = excelDateToISO(val);
        } else if (field === "totalCost") {
          mapped.totalCost = String(val).replace(/,/g, "").trim();
        } else if (field === "status") {
          mapped.status = normalizeStatus(String(val));
        } else {
          (mapped as any)[field] = String(val).trim();
        }
      }
      return mapped as IAssetPayload;
    })
    // Require the two mandatory fields so the backend won't reject the row.
    .filter((a) => a.assetNo && a.description);

  return assets;
}
